<script setup>
import { onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { getNotificationsCount, getProfile, logoutUser } from '@/api/userApi.js';
import { showNotification } from '@/helper/helper';
import { useRouter } from 'vue-router';

const { t, locale } = useI18n();
const store = useStore();
const router = useRouter();
const notificationCount = ref(0);

const changeLanguage = (lang) => {
    locale.value = lang;
    sessionStorage.setItem('lang', lang);
};

const avatarPath = ref(store.state.avatar ?? '/images/empty-avatar.svg');

watch(() => store.state.avatar, async (newAvatar) => {
    if (newAvatar) {
        avatarPath.value = newAvatar;
    }
});

const logout = async () => {
    const isImpersonating = sessionStorage.getItem('is_impersonating') === 'true';
    const originalUser = sessionStorage.getItem('original');
    if (isImpersonating && originalUser) {
        store.dispatch('loginAsOriginal');
        window.location.href = '/admin/users';
    }
    else {
        await logoutUser().finally(() => {
            store.dispatch('logout');
            router.push('/login');
        });
    }
};

const storeUserInfo = (info) => {
    const userInfo = {
        avatar: info.avatar,
        fio: info.last_name + ' ' + info.first_name,
        is_blocked: info.is_blocked
    };
    store.dispatch('setUserInfo', userInfo);
};

onMounted(async () => {
    if (store.state.token) {
        await getProfile(store.state.user_id)
            .then((res) => {
                if (res && res.data) storeUserInfo(res.data);
            });
        await getNotificationsCount()
            .then(res => {
                if (res && res.data) notificationCount.value = res.data.count;
            });
    }
});
</script>

<template>
    <header class="w-full px-8 py-2 box-border flex justify-end items-center z-[1] bg-[#127CC1] text-white">
        <v-btn
            variant="text"
            append-icon="mdi-chevron-down"
            rounded="lg"
            class="font-bold text-base uppercase text-white"
        >
            {{  
                $i18n.locale === 'ru' ? 'РУС' 
                : $i18n.locale === 'kk' ? 'ҚАЗ' 
                : 'ENG' 
            }}
            <v-menu activator="parent">
                <v-list>
                    <v-list-item 
                        title="РУС" 
                        class="uppercase"
                        @click="changeLanguage('ru')" 
                    />
                    <v-list-item 
                        title="ҚАЗ" 
                        class="uppercase"
                        @click="changeLanguage('kk')" 
                    />
                    <v-list-item 
                        title="ENG" 
                        class="uppercase"
                        @click="changeLanguage('en')" 
                    />
                </v-list>
            </v-menu>
        </v-btn>

        <v-btn 
            variant="text"
            color="#FFFFFF"
            rounded="full"
            width="64"
            height="64"
            class="mx-6"
            @click="$router.push('/admin/notifications')"
        >
            <v-badge v-if="notificationCount > 0" :content="notificationCount" color="error">
                <v-icon icon="mdi-bell" size="x-large" />
            </v-badge>
            <v-icon v-else icon="mdi-bell" size="x-large" />
        </v-btn>

        <div class="flex flex-col items-end">
            <router-link 
                to="/admin/profile" 
                class="text-lg font-semibold leading-4 block max-w-[252px] text-right"
            >
                {{ store.state.fio ?? $t('general.cabinet') }}
            </router-link>
            <span 
                class="opacity-[54%] cursor-pointer" 
                @click="logout"
            >
                {{ $t('btns.logout') }}
            </span>
        </div>
        <v-avatar
            :image="avatarPath"
            size="40"
            class="cursor-pointer ml-3"
            @click="$router.push('/admin/profile')"
        />
    </header>
</template>